/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import qs from 'qs';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { getPageContent, getSelectedInfo, getUpdatedParams } from '../utils/commonRingUtils';
import { getPageData } from '../utils/Pages';
import { ITab } from '../types/customizer';
import DesktopSidebar from '../components/Sidebar/DesktopSidebar';
import MobileSidebar from '../components/Sidebar/MobileSidebar';
import './index.scss';
import If from '../components/If';
import undoIcon from '../assets/images/Undo-Arrow.svg';
import MyGallery from '../components/MyGallery';
import AppTabs from '../components/AppTabs';
import Loading from '../components/Shared/Loader';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import { getProductDetail, createProduct, getScreensaverData } from '../redux/actions/customizer';
import { resetCreatedProduct } from '../redux/slices/customizer/customizerSlice';
import RingCustomizerHOC, { ICustomizerHOCProps, IParams, IUrl } from '../components/RingCustomizerHOC';
import URLs from '../utils/url';
import { isDoneAtBottom, urlToParams } from '../utils/basic';
import { compareParams } from '../utils/helper';
import Banner from '../components/Banner';
import ReverseIcon from '../assets/images/contrast.png';
import AppModal from '../components/AppModal';
import DiscountedPopup from '../components/DiscountedPopup';

// Screensaver configuration defaults
const SCREENSAVER_CONFIG = {
  // Enable screensaver for all users (true) or only logged-in users (false)
  ENABLE_FOR_ALL_USERS: true,
  // Fade transition duration in milliseconds
  FADE_DURATION: 500
};

/**
 * Returns true only for desktop touchscreen devices.
 * Excludes mobile and tablet by checking screen width and user agent.
 */
const isDesktopTouchscreen = (): boolean => {
  const hasTouchscreen = navigator.maxTouchPoints > 0;
  if (!hasTouchscreen) return false;

  const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches;
  if (!isLargeScreen) return false;

  // Exclude mobile/tablet user agents even if they have a large viewport
  const ua = navigator.userAgent.toLowerCase();
  const isMobileOrTablet = /android|iphone|ipad|ipod|mobile|tablet/.test(ua);

  return !isMobileOrTablet;
};

export interface ICustomizerProps {
  match: any;
  location: any;
  history: any;
  type: number;
  params: IParams;
  setParams: (params: IParams) => void;
  url: IUrl;
  isQueryHandling?: boolean;
}

const RingCustomizer = ({ location, history, type, params, url, isQueryHandling, setParams }: ICustomizerProps) => {
  const [pages, setPages] = useState<ITab[]>([]);
  const [key, setKey] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [myHistory, setMyHistory] = useState<string[]>([]);
  const [discountOpen, setDiscountOpen] = useState<boolean>(false);
  const [engraving, setEngraving] = useState('');
  const [inactivityModalOpen, setInactivityModalOpen] = useState<boolean>(false);
  const [isFading, setIsFading] = useState<boolean>(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const screensaverFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (params.otherQueryParams?.bestSeller) {
      setDiscountOpen(true);
    }
  }, [params.otherQueryParams?.bestSeller]);

  const dispatch = useAppDispatch();
  const {
    product,
    isFetching,
    createdProduct,
    bestSellerProduct: bProduct,
    screensaverData
  } = useAppSelector((state: RootState) => ({ ...state.customizer }));
  const initialParamsRef = useRef<IParams>(params);

  useEffect(() => {
    // Fetch screensaver data once on mount
    if (!screensaverFetchedRef.current) {
      screensaverFetchedRef.current = true;
      dispatch(getScreensaverData());
    }
  }, [dispatch]);

  useEffect(() => {
    if (url.ring_url) {
      dispatch(getProductDetail({ url: url.ring_url, type }));
    }
  }, [url.ring_url]);

  useEffect(() => {
    // Clean up inactivity timer on unmount
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  // Toggle body class to hide third-party overlays during screensaver
  useEffect(() => {
    if (inactivityModalOpen) {
      document.body.classList.add('screensaver-active');
    } else {
      document.body.classList.remove('screensaver-active');
    }
    return () => {
      document.body.classList.remove('screensaver-active');
    };
  }, [inactivityModalOpen]);

  useEffect(() => {
    if (!product) {
      return;
    }
    if (url.ring_url && product.ring_options_url !== url.ring_url) {
      navigateToUrl(product.ring_options_url, true);
    }

    // const tabData = getPageData(product, params.pathParams.pathPrefix === 'helzberg');
    const tabData = getPageData({
      product,
      type,
      subpath: '',
      ring_type: params.pathParams.ring_type,
      storeData: { show_women_builder: false, show_signet_builder: true, show_collegiate_builder: true },
      isBuildFromScratch: params?.queryParams?.isBuildFromScratch === 'true'
    });
    const activekey = getActiveTab(tabData);

    setPages(tabData);
    if (!key) {
      setKey(activekey);
    }
  }, [product]);

  useEffect(() => {
    if (createdProduct) {
      const productUrl = `${URLs.CUSTOM_PDP}?pid=${createdProduct.ring_category_sku}&lbuuid=${createdProduct.id}`;
      dispatch(resetCreatedProduct());
      window.open(productUrl, '_self');
    }
  }, [createdProduct]);

  useEffect(() => {
    const { pathParams: initialPathParams, queryParams: initialQueryParams } = initialParamsRef.current;
    if (Object.keys(initialPathParams).length === 0 && Object.keys(initialQueryParams).length === 0) {
      initialParamsRef.current = { pathParams: { ...params.pathParams }, queryParams: { ...params.queryParams } };
    }
  }, [params]);

  useEffect(() => {
    if (params.otherQueryParams?.base_ring_url) {
      const baseUrl = params.otherQueryParams.base_ring_url;
      initialParamsRef.current = urlToParams(baseUrl);
    }
  }, [params.otherQueryParams?.base_ring_url]);

  const navigateToUrl = (url: string, replace = true) => {
    let navigateUrl = url; // In future if you want add any path to ring url
    /**
     * In ring url, Attach some path and query params
     * If there is pathPrefix, attach before url
     * If there is queryHandling, attach entire url as queryParam (ring_url)
     * If there is otherQueryString, attach end of url
     */
    const {
      pathParams: { pathPrefix }
    } = params;
    if (pathPrefix) {
      navigateUrl = `/${pathPrefix}${navigateUrl}`;
    }

    if (isQueryHandling) {
      navigateUrl = `${location.pathname}?ring_url=${encodeURIComponent(navigateUrl)}`;
    }
    const otherQueryString = new URLSearchParams(params.otherQueryParams).toString();
    if (otherQueryString) {
      navigateUrl = `${navigateUrl}&${otherQueryString}`;
    }

    addHistory(navigateUrl, replace);
    if (replace) {
      history.replace(navigateUrl); // After url is navigated it calls a useffect of location dependencies
    } else {
      history.push(navigateUrl); // After url is navigated it calls a useffect of location dependencies
    }
  };

  const handleUrlChange = ({ pathParams, queryParams }: IParams) => {
    let URL = '';
    if (type === 1) {
      const { profile, width, ring_materials } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/rings/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}?${searchString}`;
    } else if (type === 2) {
      const { styles, ring_materials, color_arrangements, stone_quantities, variants } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/women/rings/styles/${styles}/ring_materials/${ring_materials}/color_arrangements/${color_arrangements}/stone_quantities/${stone_quantities}/variants/${variants}?${searchString}`;
    } else if (type === 3) {
      const { ring_type, ring_materials } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/rings/${ring_type}/ring_materials/${ring_materials}?${searchString}`;
    }
    navigateToUrl(URL, false); // push new URL
  };

  const handleUpdate = (tobeUpdated: any) => {
    const updatedParams = getUpdatedParams(tobeUpdated, params);
    handleUrlChange(updatedParams);
  };

  const getActiveTab = (items: ITab[]) => {
    const selectedTab = items.find((tab) => tab.isSelected);
    let key = '';
    if (selectedTab) {
      key = selectedTab.eventKey;
    } else {
      key = items?.[0]?.eventKey || '';
    }
    return key;
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  const handleActiveTab = (key: string, toggleVisibility: boolean) => () => {
    setKey(key);
    if (toggleVisibility) {
      toggleOpen();
    }
  };

  const addHistory = (url: string, replace: boolean) => {
    setMyHistory([url]);
  };

  const removeHistory = () => {
    setMyHistory([]);
  };

  const handleUndo = () => {
    if (myHistory.length) {
      history.goBack();
      removeHistory();
    }
  };

  const handleDone = async () => {
    const paramsChanged = compareParams(initialParamsRef.current, params);
    const length = paramsChanged.length;
    let changes_made;
    /*
    if (length === 0) {
      if (params.otherQueryParams?.product_id) {
        if (params.otherQueryParams?.redirect_url) {
          window.location.href = params.otherQueryParams.redirect_url;
        } else {
          toast.info('No changes made');
        }
        return;
      } else {
        changes_made = 'full_customization';
      }
    } else {
    }
    */
    if (length === 0) {
      changes_made = 'no_changes';
    } else if (length === 1 && paramsChanged.includes('size')) {
      changes_made = 'only_size';
    } else if (paramsChanged.includes('engraving_new') && params.queryParams.engraving_new === 'T') {
      toast.warn('Select any font style and Please enter text');
      return;
    } else {
      changes_made = 'full_customization';
    }

    let ring_url = product.ring_options_url;
    if (params.pathParams.pathPrefix) {
      ring_url = `/${params.pathParams.pathPrefix}${ring_url}`;
    }
    const data = {
      ring_url: ring_url,
      base_ring_url: params.otherQueryParams?.base_ring_url || null,
      ring_images: product.images,
      ring_description: product.description,
      ring_sku: product.sku,
      price: product.price,
      tcw: product.tcw,
      title: product.title,
      ring_category_sku: product.helzberg_custom_sku,
      magic_number: product.magic_number,
      changes_made,
      client_name: params.pathParams.pathPrefix,
      custom_sku: params.otherQueryParams?.product_id || null,
      ring_options: getPageContent(pages, 'payload')
    };
    dispatch(createProduct({ data }));
  };

  const handleBestSellerDone = () => {
    const paramsChanged = compareParams(initialParamsRef.current, params);
    const length = paramsChanged.length;
    let changes_made;
    if (length === 0) {
      changes_made = 'no_changes';
    } else if (length === 1 && paramsChanged.includes('size')) {
      changes_made = 'only_size';
    } else if (paramsChanged.includes('engraving_new') && params.queryParams.engraving_new === 'T') {
      toast.warn('Select any font style and Please enter text');
      return;
    } else {
      changes_made = 'full_customization';
    }

    let ring_url = bProduct?.ring_options_url;
    if (params.pathParams.pathPrefix) {
      ring_url = `/${params.pathParams.pathPrefix}${ring_url}`;
    }

    const pageData = getPageData({
      product: bProduct,
      type,
      subpath: '',
      ring_type: params.pathParams.ring_type,
      storeData: { show_women_builder: false, show_signet_builder: true, show_collegiate_builder: true },
      isBuildFromScratch: false
    });

    const data = {
      bestseller_id: bProduct?.bestseller_id,
      msrp: bProduct?.msrp,
      engraving: engraving,

      ring_url: ring_url,
      base_ring_url: params.otherQueryParams?.base_ring_url || null,
      ring_images: bProduct?.images,
      ring_description: bProduct?.description,
      ring_sku: bProduct?.sku,
      price: bProduct?.price,
      tcw: bProduct?.tcw,
      title: bProduct?.title,
      ring_category_sku: bProduct?.helzberg_custom_sku,
      magic_number: bProduct?.magic_number,
      changes_made,
      client_name: params.pathParams.pathPrefix,
      custom_sku: params.otherQueryParams?.product_id || null,
      ring_options: getPageContent(pageData, 'payload')
    };
    console.log(data);
    dispatch(createProduct({ data }));
  };

  const pathPrefix = params.pathParams.pathPrefix;

  const handleEngravingReversePattern = () => {
    const { selectedOption } = engravingInfo;
    if (selectedOption.is_reverse_selected) {
      handleUpdate({ engraving_pattern: selectedOption.sku });
    } else {
      handleUpdate({ engraving_pattern: `${selectedOption.sku}REV` });
    }
  };

  const handleLogoReversePattern = () => {
    const { selectedOption } = SignetEngravingInfo;
    if (selectedOption.is_reverse_selected) {
      handleUpdate({ signet_engraving: selectedOption.sku });
    } else {
      handleUpdate({ signet_engraving: `${selectedOption.sku}REV` });
    }
  };

  const handleBestSeller = (id: string) => {
    setParams({ ...params, otherQueryParams: { ...params.otherQueryParams, bestSeller: id } });
  };

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Don't start timer if screensaver is already open
    if (inactivityModalOpen) {
      return;
    }

    // Check if screensaver should be enabled
    const shouldEnable = SCREENSAVER_CONFIG.ENABLE_FOR_ALL_USERS;
    if (!shouldEnable) {
      return;
    }

    // Only enable screensaver on desktop touchscreen devices
    if (!isDesktopTouchscreen()) {
      return;
    }

    // Only enable screensaver if API data indicates it's active and has a video
    if (!screensaverData || !screensaverData.active || !screensaverData.video_path) {
      return;
    }

    const timeoutSeconds = screensaverData.time_in_seconds;

    // Set new timer (convert seconds to milliseconds)
    inactivityTimerRef.current = setTimeout(() => {
      setIsFading(true);
      setTimeout(() => {
        setInactivityModalOpen(true);
        setIsFading(false);
        // Ensure video plays when screensaver opens
        if (videoRef.current) {
          videoRef.current.play().catch(console.error);
        }
      }, SCREENSAVER_CONFIG.FADE_DURATION);
    }, timeoutSeconds * 1000);
  }, [inactivityModalOpen, screensaverData]);

  const closeInactivityModal = useCallback(() => {
    setIsFading(true);
    setTimeout(() => {
      setInactivityModalOpen(false);
      setIsFading(false);
      resetInactivityTimer();
    }, SCREENSAVER_CONFIG.FADE_DURATION);
  }, [resetInactivityTimer]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    // Start initial timer
    resetInactivityTimer();

    // Activity event handlers
    const handleActivity = () => {
      if (inactivityModalOpen) {
        closeInactivityModal();
      } else {
        resetInactivityTimer();
      }
    };

    // Add event listeners for user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('click', handleActivity);

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('click', handleActivity);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [inactivityModalOpen, resetInactivityTimer, closeInactivityModal]);

  const selectedPage = pages.find((item) => item.eventKey === key);
  const engravingInfo = key === 'engraving' ? getSelectedInfo(selectedPage) : null;
  const SignetEngravingInfo = key === 'logo' || key === 'outsideFeature_1' ? getSelectedInfo(selectedPage) : null;

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          href={
            pathPrefix ? `${process.env.PUBLIC_URL}/${pathPrefix}-favicon.ico` : `${process.env.PUBLIC_URL}/favicon.ico`
          }
        />
        <title>Ring builder</title>
      </Helmet>
      <Banner pathPrefix={pathPrefix} />
      <If condition={!isDoneAtBottom(pathPrefix || '')}>
        <div className="tw-flex tw-items-center tw-justify-center tw-px-3 tw-pt-3 tw-w-full tw-top-0 tw-bg-white lg:tw-hidden">
          <button
            className="tw-bg-brand-color tw-text-tertiary-color tw-text-base tw-px-9 tw-py-2 tw-rounded-tertiary tw-normal-case tw-font-[inherit]"
            onClick={handleDone}
          >
            Done - {product?.price}
          </button>
        </div>
      </If>
      <div className="secondary-font-family customizer_main_container tw-flex-1 tw-flex">
        <DesktopSidebar
          pages={pages}
          selectedTab={key}
          handleActiveTab={handleActiveTab}
          handleDone={handleDone}
          price={product?.price}
          pathPrefix={pathPrefix}
        />
        <div className="tw-flex-1 tw-w-0 tw-flex tw-flex-col">
          {' '}
          {/* tw-w-0 is most important it will set overflow width to hidden. */}
          <div
            className={clsx('gallery_container tw-relative tw-pb-5 tw-pt-5 lg:tw-pt-10', {
              'lg:tw-hidden': ['summaryInfo', 'summary', 'data'].includes(key)
            })}
          >
            <MyGallery images={product?.images || []} />
            <div className="tw-absolute tw-bottom-1 tw-text-center tw-cursor-pointer tw-left-3 sm:tw-left-6 lg:tw-left-20 tw-flex  xs:tw-flex-col lg:tw-flex-row xs:tw-gap-2 lg:tw-gap-5 secondary-font-family">
              <If condition={myHistory.length}>
                <div className="undo_button" onClick={handleUndo} role="presentation">
                  <img src={undoIcon} alt="undo_icon" className="tw-inline-block tw-w-5" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm">Undo</div>
                </div>
              </If>
              <If condition={key === 'engraving' && engravingInfo?.selectedOption?.has_inside_reversed_option}>
                <div onClick={handleEngravingReversePattern} role="presentation">
                  <img src={ReverseIcon} alt="undo_icon" className="tw-inline-block tw-w-6" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm tw-m-0.5">
                    Reverse <br />
                    Pattern
                  </div>
                </div>
              </If>
              <If
                condition={
                  (key === 'logo' || key === 'outsideFeature_1') &&
                  SignetEngravingInfo?.selectedOption?.has_outside_reversed_option
                }
              >
                <div onClick={handleLogoReversePattern} role="presentation">
                  <img src={ReverseIcon} alt="undo_icon" className="tw-inline-block tw-w-6" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm tw-m-0.5">
                    Reverse <br />
                    Pattern
                  </div>
                </div>
              </If>
            </div>
          </div>
          <div className={clsx(`tw-relative customizer_container tw-flex-1 tw-flex tw-flex-col ${key}`)}>
            <div id="main-content" className={clsx('tw-flex-1', { 'tw-hidden': open })}>
              {pages.length > 0 && (
                <AppTabs
                  tabs={pages}
                  tabStyle="single"
                  onChange={handleUpdate}
                  isFetching={isFetching}
                  activeKey={key}
                  onChangeParentActiveKey={setKey}
                  pathPrefix={params.pathParams.pathPrefix}
                  navigateToUrl={navigateToUrl}
                />
              )}
            </div>
            <If condition={isDoneAtBottom(params.pathParams.pathPrefix || '')}>
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-3 tw-py-4 tw-w-full tw-top-0 tw-bg-gray-50 lg:tw-hidden">
                <div className="tw-w-3/4 tw-flex tw-justify-between tw-pb-2">
                  <label>Estimated Total</label>
                  <div>{product?.price}</div>
                </div>
                <button
                  className="tw-bg-brand-color tw-text-tertiary-color tw-text-base tw-w-3/4 tw-py-2 tw-rounded-tertiary tw-normal-case tw-font-[inherit]"
                  onClick={handleDone}
                >
                  Done
                </button>
              </div>
            </If>
            {!open && (
              <div
                role="presentation"
                className="tw-absolute tw-left-3 tw-top-4 tw-cursor-pointer tw-flex lg:tw-hidden"
                onClick={toggleOpen}
              >
                <i className="fa fa-ellipsis-vertical tw-pr-0.5 tw-text-brand-color" />
                <i className="fa fa-bars tw-text-brand-color" />
                {/* <span className="tw-text-base tw-font-bold tw-ml-3">All Steps</span> */}
              </div>
            )}
            <AppModal size="lg" isOpen={open} onClose={toggleOpen} hideCloseIcon={true}>
              <MobileSidebar
                pages={pages}
                selectedTab={key}
                handleActiveTab={handleActiveTab}
                toggleOpen={toggleOpen}
                price={product?.price}
              />
            </AppModal>
          </div>
        </div>
        <AppModal size="xxl" isOpen={discountOpen} onClose={() => setDiscountOpen(false)}>
          <DiscountedPopup
            id={params.otherQueryParams?.bestSeller}
            handleBestSeller={handleBestSeller}
            engraving={engraving}
            setEngraving={setEngraving}
            handleBestSellerDone={handleBestSellerDone}
            product={product}
            navigateToUrl={navigateToUrl}
            onClose={() => setDiscountOpen(false)}
          />
        </AppModal>
        {/* Screensaver - Fullscreen overlay */}
        {inactivityModalOpen && screensaverData?.active && screensaverData?.video_path && (
          <div
            className="screensaver-overlay"
            onClick={closeInactivityModal}
            role="presentation"
            style={{
              transition: `opacity ${SCREENSAVER_CONFIG.FADE_DURATION}ms ease-in-out`,
              opacity: isFading ? 0 : 1
            }}
          >
            <video
              ref={videoRef}
              src={`${screensaverData.video_path}${
                screensaverData.video_path.indexOf('?') !== -1 ? '&' : '?'
              }rand=${Math.random().toString(12)}`}
              autoPlay
              muted
              loop
              playsInline
              onError={(e) => {
                console.error('Screensaver video failed to load:', e);
                // Optionally show a fallback image or message
              }}
              className="screensaver-video"
            />
            <div className="tw-absolute tw-bottom-8 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-text-white tw-text-sm tw-opacity-70 tw-z-10">
              Click anywhere to continue
            </div>
          </div>
        )}
        <Loading timedOut={isFetching} />
      </div>
    </>
  );
};

const RingCustomizerContainer: FC<ICustomizerHOCProps> = RingCustomizerHOC(RingCustomizer);

export default RingCustomizerContainer;
